import type React from 'react';
import { Box } from '@mui/material';
import ApprovalStatusBadge from '@/components/Approval/ApprovalStatusBadge';
import {
  RowAuthor,
  RowBelow,
  RowDate,
  RowDivider,
  RowLead,
  RowMeta,
  RowMetaCluster,
  RowMetaInfo,
  RowMetaTrailing,
  RowRoot,
  RowTitle,
} from './styles';

// 연관품의 4화면(조회/검색결과/선택/변경이력) 공통 항목 행 — 화면 차이는 prop 분기, chrome 은 rootComponent 주입
type RelatedItemRowProps = {
  title: string;
  href?: string;
  // history=변경이력 전용 파랑, view=검정 링크(hover underline), plain=검정 비링크
  titleTone?: 'view' | 'history' | 'plain';
  strikethrough?: boolean;
  leading?: React.ReactNode;
  status?: string | null;
  author?: string;
  date?: string;
  trailing?: React.ReactNode;
  below?: React.ReactNode;
  dividerTone?: 'view' | 'default';
  layout?: 'stack' | 'inline';
  // 화면별 chrome(bg/border/padding/hover/clickable) 주입 — styled(RowRoot) 변형을 넘김.
  rootComponent?: React.ElementType;
  className?: string;
  // 검색결과 키보드 nav 포커스 타겟 마커 (querySelector 대상)
  dataResultItem?: boolean;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
  role?: string;
  tabIndex?: number;
};

const RelatedItemRow = ({
  title,
  href,
  titleTone = 'plain',
  strikethrough,
  leading,
  status,
  author,
  date,
  trailing,
  below,
  dividerTone = 'default',
  layout = 'stack',
  rootComponent: RootComponent = RowRoot,
  className,
  dataResultItem,
  onClick,
  onKeyDown,
  role,
  tabIndex,
}: RelatedItemRowProps) => {
  const isLink = !!href;
  const titleNode = (
    <RowTitle
      as={isLink ? 'a' : 'span'}
      titleTone={titleTone}
      strikethrough={strikethrough}
      {...(isLink
        ? { href, target: '_blank', rel: 'noopener noreferrer' }
        : {})}
    >
      {title}
    </RowTitle>
  );

  // inline: below 를 title 아래 두기 위해 lead 를 column 으로 감싸 badge 와 top-align 유지
  const lead =
    layout === 'inline' && below ? (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          flex: '1 1 auto',
          minWidth: 0,
        }}
      >
        <RowLead>
          {leading}
          {titleNode}
        </RowLead>
        <RowBelow>{below}</RowBelow>
      </Box>
    ) : (
      <RowLead>
        {leading}
        {titleNode}
      </RowLead>
    );

  const meta = (
    <RowMeta layout={layout}>
      <RowMetaCluster>
        <ApprovalStatusBadge status={status} />
      </RowMetaCluster>
      {(author != null || date != null || trailing) && (
        <RowMetaTrailing className="related-row-meta-trailing">
          {(author != null || date != null) && (
            <RowMetaInfo>
              {author != null && <RowAuthor>{author}</RowAuthor>}
              {author != null && date != null && (
                <RowDivider tone={dividerTone} />
              )}
              {date != null && <RowDate>{date}</RowDate>}
            </RowMetaInfo>
          )}
          {trailing}
        </RowMetaTrailing>
      )}
    </RowMeta>
  );

  return (
    <RootComponent
      layout={layout}
      className={className}
      data-result-item={dataResultItem ? '' : undefined}
      onClick={onClick}
      onKeyDown={onKeyDown}
      role={role}
      tabIndex={tabIndex}
    >
      {lead}
      {meta}
      {layout === 'stack' && below ? <RowBelow>{below}</RowBelow> : null}
    </RootComponent>
  );
};

export default RelatedItemRow;
